import { formatLastUpdate } from "../../utils/formatLastUpdate";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config.js";
import DeleteModal from "../common/DeleteModal";
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../../pages/login/UserContextPersistent";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "../../hooks/useUserInfo";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import { hasLayoutPermission } from "../../config/vehiclesModuleCatalog";
import {
  buildFilterWidgetsFromLayout,
  getActionBarWidgets,
  getPageTitleKey,
} from "../../layout/moduleLayoutUtils";
import SubTitleNew from "../common/New/SubTitleNew";
import StatsCard from "../common/New/StatsCard.jsx";
import DynamicTable, {
  TableLastUpdate,
  TableStatusBadge,
} from "../common/New/DynamicTable";
import {
  CUSTOM_DATE_OPTION,
  DASHBOARD_DATE_OPTIONS,
} from "../vehicle/DashboardGraph.jsx";
import { getOrganizationId } from "../manageData/reportsApi.js";
import StopsDashboard, {
  buildStopVisitStats,
  fetchStopVisitReport,
  formatStopDwell,
  getStopRouteNames,
  getStopRoutes,
  matchStopStats,
  stopHasAssignedRoute,
} from "./StopsDashboard";
import StopsAddMain from "./StopsAddMain";
import {
  clearCreateIntent,
  readCreateIntent,
} from "../../utils/moduleCreateIntent";
import HailIcon from "@mui/icons-material/Hail";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Alert, Box } from "@mui/material";
import "../vehicle/Vehicle.css";
import "./StopsDashboard.css";

const isRouteEyeAdmin = () => {
  const userState = localStorage.getItem("userState");
  if (!userState) return false;
  try {
    const user = JSON.parse(userState).user;
    return user.organizationId === 5 && user.email === "sales@routeeye.io";
  } catch {
    return false;
  }
};

const STOP_TABLE_BATCH_SIZE = 10;

const STOP_PAGE_CACHE_MS = 60 * 1000;
const stopListCache = new Map();
const stopHaltCache = new Map();

const toReportDateOption = (value) =>
  String(value || "today")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

const rangeIncludesToday = (dateOption, customRange = {}) => {
  const option = toReportDateOption(dateOption);
  if (option === "yesterday") return false;
  if (option !== "custom") return true;
  if (!customRange.start || !customRange.end) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(customRange.start);
  const end = new Date(customRange.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return true;
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return start <= today && end >= today;
};

export default function Stops({ variant } = {}) {
  const { t } = useTranslation();
  const [stopsData, setStopsData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    route: "",
    stop: "",
    status: "",
    type: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedStopId, setSelectedStopId] = useState(null);
  const [selectedStopData, setSelectedStopData] = useState(null);
  const { state, organizationInactive } = useUser();
  const { userId } = useUserInfo();

  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermission();
  const isSchool =
    variant === "school" || location.pathname.startsWith("/school-stops");
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const useModuleLayout = useJsonLayout && !isRouteEyeAdmin();
  const { layout: moduleLayout } = usePageLayout(
    useModuleLayout
      ? isSchool
        ? PAGE_IDS.SCHOOL_STOPS
        : PAGE_IDS.FLEET_STOPS
      : null,
  );

  const actionBarWidgets = useMemo(
    () => (moduleLayout ? getActionBarWidgets(moduleLayout) : []),
    [moduleLayout],
  );

  const layoutFilterWidgets = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFilterWidgetsFromLayout(moduleLayout);
  }, [moduleLayout]);

  // Enhanced permission check function
  const checkPermission = (requiredPath, requiredAction = null) => {
    // An empty list means features were not loaded yet (for example right
    // after registration). Treat that like "no feature list" and allow the
    // page to open, the same way Vehicles and Routes do.
    if (!state?.features?.length || !requiredPath) {
      return true;
    }

    return state.features.some((feature) => {
      const pathMatches = feature.api_path.includes(requiredPath);
      return requiredAction
        ? pathMatches && feature.action === requiredAction
        : pathMatches;
    });
  };

  const [filterConfig, setFilterConfig] = useState([]);
  const [routeOptions, setRouteOptions] = useState([]);
  const [stopOptions, setStopOptions] = useState([]);
  const [dashboardDateOption, setDashboardDateOption] = useState("today");
  const [dashboardCustomRange, setDashboardCustomRange] = useState({
    start: "",
    end: "",
  });
  const [dashboardError, setDashboardError] = useState("");
  const [haltReport, setHaltReport] = useState(null);
  const [previousHaltReport, setPreviousHaltReport] = useState(null);
  const [isHaltLoading, setIsHaltLoading] = useState(true);
  const [showCreateStop, setShowCreateStop] = useState(false);
  const [expandedStopId, setExpandedStopId] = useState(null);
  const [visibleStopCount, setVisibleStopCount] = useState(STOP_TABLE_BATCH_SIZE);

  useEffect(() => {
    if (layoutFilterWidgets && !isRouteEyeAdmin()) {
      setFilterConfig(
        layoutFilterWidgets.map((widget) => ({
          id: widget.filterKey,
          label: widget.labelKey,
          helperText: `Select ${String(widget.labelKey || "").toLowerCase()}`,
          options:
            widget.filterKey === "route" || widget.filterKey === "routes"
              ? routeOptions
              : widget.filterKey === "stop" || widget.filterKey === "stopName"
                ? stopOptions
                : (widget.options || []).map((option) => ({
                    value: option.value,
                    label: option.label,
                  })),
        })),
      );
      return;
    }

    if (isRouteEyeAdmin()) {
      // Admin filters: Organization, Site, Routes, Stop
      setFilterConfig([
        {
          id: "organization",
          label: "Organization",
          helperText: "Select organization",
          options: organizationOptions,
        },
        {
          id: "site",
          label: "Site",
          helperText: "Select site",
          options: siteOptions,
        },
        {
          id: "route",
          label: "routes",
          helperText: "Select Route",
          options: [],
        },
        {
          id: "stop",
          label: "stops",
          helperText: "Select Stop",
          options: [],
        },
      ]);
    } else {
      // Regular user filters: Route, Stop
      setFilterConfig([
        {
          id: "route",
          label: "routes",
          helperText: "Select Route",
          options: [],
        },
        {
          id: "stop",
          label: "stops",
          helperText: "Select Stop",
          options: [],
        },
      ]);
    }
  }, [layoutFilterWidgets, organizationOptions, siteOptions, routeOptions, stopOptions]);

  const fetchOrganizationsAndSites = async () => {
    try {
      // Fetch organizations
      const orgResponse = await axios.get(`${config.baseURL}/organization`, {
        withCredentials: true,
      });

      let organizations = [];
      if (Array.isArray(orgResponse.data)) {
        organizations = orgResponse.data;
      } else if (Array.isArray(orgResponse.data.organizations)) {
        organizations = orgResponse.data.organizations;
      }

      const orgOptions = organizations.map((org) => ({
        value: org.id.toString(),
        label: org.name || `Organization ${org.id}`,
      }));
      setOrganizationOptions(orgOptions);

      // Fetch sites
      const siteResponse = await axios.get(`${config.baseURL}/site`, {
        withCredentials: true,
      });

      let sites = [];
      if (Array.isArray(siteResponse.data)) {
        sites = siteResponse.data;
      } else if (Array.isArray(siteResponse.data.sites)) {
        sites = siteResponse.data.sites;
      }

      const siteOptions = sites.map((site) => ({
        value: site.id.toString(),
        label: site.siteId || site.name || `Site ${site.id}`,
      }));
      setSiteOptions(siteOptions);
    } catch (error) {
      console.error("Error fetching organizations/sites:", error);
    }
  };

  // Function to fetch routes by coordinates
  const getRouteByStopCoordinates = async (lat, lng, orgId) => {
    try {
      const response = await axios.get(`${config.baseURL}/route/coordinates/`, {
        params: {
          longitude: lng,
          latitude: lat,
          organizationId: orgId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching routes by coordinates:", error);
      return [];
    }
  };

  // Fetch stops data from API on component mount
  const loadStops = useCallback(async (force = false) => {
    if (!checkPermission("/api/stop/organization", "GET")) {
      console.log("No permission to view stops");
      navigate("/dashboard");
      return;
    }

    const listCacheKey = String(getOrganizationId() || "");
    if (force) stopListCache.delete(listCacheKey);
    const cachedList = stopListCache.get(listCacheKey);
    if (cachedList && Date.now() - cachedList.fetchedAt < STOP_PAGE_CACHE_MS) {
      setStopsData(cachedList.stops);
      setRouteOptions(cachedList.routeOptions);
      setStopOptions(cachedList.stopOptions);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(!cachedList);
      if (cachedList) {
        setStopsData(cachedList.stops);
        setRouteOptions(cachedList.routeOptions);
        setStopOptions(cachedList.stopOptions);
      }
      if (isRouteEyeAdmin()) {
        await fetchOrganizationsAndSites();
      }
      const stopsResponse = await axios.get(
        isRouteEyeAdmin()
          ? `${config.baseURL}/stop`
          : `${config.baseURL}/stop/organization`,
        { withCredentials: true }
      );
      const stops = stopsResponse.data;

      const stopsWithRoutes = await Promise.all(
        stops.map(async (stop) => {
          const routes = await getRouteByStopCoordinates(
            stop.latitude,
            stop.longitude,
            stop.organizationId
          );
          return { ...stop, routes };
        })
      );

      setStopsData(stopsWithRoutes);

      const uniqueRoutes = [];
      const routeIds = new Set();
      const uniqueStops = [];
      const stopIds = new Set();

      stopsWithRoutes.forEach((stop) => {
        if (stop.routes && stop.routes.length > 0) {
          stop.routes.forEach((route) => {
            const routeId = route.id || route.Id || route.ID;
            const routeName = route.name || route.Name || `Route ${routeId}`;

            if (routeId && !routeIds.has(routeId)) {
              routeIds.add(routeId);
              uniqueRoutes.push({
                value: routeId.toString(),
                label: routeName,
              });
            }
          });
        }

        const stopId = stop.id || stop.Id || stop.ID;
        const stopName = stop.name || stop.Name || `Stop ${stopId}`;

        if (stopId && !stopIds.has(stopId)) {
          stopIds.add(stopId);
          uniqueStops.push({
            value: stopId.toString(),
            label: stopName,
          });
        }
      });

      setRouteOptions(uniqueRoutes);
      setStopOptions(uniqueStops);
      stopListCache.set(listCacheKey, {
        stops: stopsWithRoutes,
        routeOptions: uniqueRoutes,
        stopOptions: uniqueStops,
        fetchedAt: Date.now(),
      });
      setIsLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("Unauthorized request");
        navigate("/");
        return;
      }
      console.error("Error fetching initial data:", error.response);
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadStops();
  }, [loadStops]);

  const handleDashboardDateRangeChange = useCallback((range) => {
    setDashboardError("");
    if (range?.type === "custom") {
      setDashboardDateOption(CUSTOM_DATE_OPTION);
      setDashboardCustomRange({
        start: range.startDate || "",
        end: range.endDate || "",
      });
      return;
    }

    setDashboardDateOption(toReportDateOption(range?.value || range?.label));
    setDashboardCustomRange({ start: "", end: "" });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const isCustom = dashboardDateOption === CUSTOM_DATE_OPTION;
    if (isCustom && !(dashboardCustomRange.start && dashboardCustomRange.end)) {
      setHaltReport(null);
      setPreviousHaltReport(null);
      setIsHaltLoading(false);
      return undefined;
    }

    const loadHaltReports = async (background = false) => {
      const haltCacheKey = [
        getOrganizationId() || "",
        filters.site || "",
        dashboardDateOption,
        dashboardCustomRange.start || "",
        dashboardCustomRange.end || "",
      ].join("|");
      const cachedHalt = stopHaltCache.get(haltCacheKey);
      const haltCacheIsFresh =
        cachedHalt && Date.now() - cachedHalt.fetchedAt < STOP_PAGE_CACHE_MS;
      if (cachedHalt) {
        if (!background) {
          setHaltReport(cachedHalt.halt);
          setPreviousHaltReport(cachedHalt.previousHalt);
          setIsHaltLoading(false);
        }
        if (!background && haltCacheIsFresh) return;
      } else if (!background) {
        setIsHaltLoading(true);
        setPreviousHaltReport(null);
      }
      setDashboardError("");
      const reportParams = {
        orgId: getOrganizationId(),
        siteId: filters.site,
        dateOption: dashboardDateOption,
        customStartDate: isCustom ? dashboardCustomRange.start : undefined,
        customEndDate: isCustom ? dashboardCustomRange.end : undefined,
      };

      try {
        const halt = await fetchStopVisitReport(reportParams);
        if (cancelled) return;
        setHaltReport(halt);
        setIsHaltLoading(false);
        const previousHalt =
          dashboardDateOption === "today"
            ? await fetchStopVisitReport({ ...reportParams, dateOption: "yesterday" }).catch(() => null)
            : null;
        if (cancelled) return;
        setPreviousHaltReport(previousHalt);
        stopHaltCache.set(haltCacheKey, {
          halt,
          previousHalt,
          fetchedAt: Date.now(),
        });
        setDashboardError("");
      } catch (error) {
        console.error("Failed to load stop visit reports:", error);
        if (!cancelled && !cachedHalt) {
          setHaltReport(null);
          setPreviousHaltReport(null);
          const backendErrorMessage =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message;
          if (backendErrorMessage) {
            setDashboardError(backendErrorMessage);
          }
        }
      } finally {
        if (!cancelled) setIsHaltLoading(false);
      }
    };

    loadHaltReports();
    const refreshTimer = rangeIncludesToday(dashboardDateOption, dashboardCustomRange)
      ? window.setInterval(() => {
          if (!cancelled) loadHaltReports(true);
        }, STOP_PAGE_CACHE_MS)
      : null;
    return () => {
      cancelled = true;
      if (refreshTimer) window.clearInterval(refreshTimer);
    };
  }, [dashboardDateOption, dashboardCustomRange.start, dashboardCustomRange.end, filters.site]);

  const visitStats = useMemo(
    () => buildStopVisitStats(haltReport),
    [haltReport],
  );
  const previousVisitStats = useMemo(
    () => buildStopVisitStats(previousHaltReport),
    [previousHaltReport],
  );

  const getStopStatusMeta = (stop) => {
    if (stopHasAssignedRoute(stop)) {
      return { label: "Active", tone: "green" };
    }
    return { label: "Unassigned", tone: "gray" };
  };

  const formatDataForTable = (data) => {
    return data.map((stop) => {
      const stats = matchStopStats(stop, visitStats);
      const statusMeta = getStopStatusMeta(stop);

      return {
        id: stop.id,
        stopCode: stop.stopId || "N/A",
        stopName: stop.name || "N/A",
        organizationName: stop.organization?.name || "N/A",
        siteID: stop.site?.siteId || "N/A",
        routes: getStopRouteNames(stop),
        status: statusMeta.label,
        statusTone: statusMeta.tone,
        visits: stats.visits || 0,
        avgDwell: formatStopDwell(stats.avgDwell || 0),
        lastVehicle: stats.lastVehicle || "--",
        lastUpdate: formatLastUpdate(stop.updatedAt, t),
        lastUpdateTone: statusMeta.tone === "green" ? "green" : statusMeta.tone === "red" ? "red" : "amber",
        type: stop.type || "STOP",
        originalData: stop,
      };
    });
  };

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  useEffect(() => {
    setVisibleStopCount(STOP_TABLE_BATCH_SIZE);
  }, [filters, searchQuery]);

  // Filter data based on search query and filters
  const filteredStopsData = stopsData.filter((stop) => {
    const selectedRouteFilter = filters.route || filters.routes;
    const selectedStopFilter = filters.stop || filters.stopName;
    // Admin filters
    if (isRouteEyeAdmin()) {
      const organizationMatch =
        !filters.organization ||
        (stop.organization?.id &&
          String(stop.organization.id) === String(filters.organization));

      const siteMatch =
        !filters.site ||
        (stop.site?.id && String(stop.site.id) === String(filters.site));

      const routeMatch =
        !selectedRouteFilter ||
        (stop.routes &&
          stop.routes.some(
            (route) => String(route.id) === String(selectedRouteFilter)
          ));

      const stopId = stop.id || stop.Id || stop.ID;
      const stopMatch =
        !selectedStopFilter || String(stopId) === String(selectedStopFilter);

      const derivedStatus = getStopStatusMeta(stop).label;
      const statusMatch =
        !filters.status || derivedStatus.toUpperCase() === String(filters.status).toUpperCase();

      if (!organizationMatch || !siteMatch || !routeMatch || !stopMatch || !statusMatch) {
        return false;
      }
    } else {
      // Regular user filters
      const routeMatch =
        !selectedRouteFilter ||
        (stop.routes &&
          stop.routes.some(
            (route) => String(route.id) === String(selectedRouteFilter)
          ));

      const stopId = stop.id || stop.Id || stop.ID;
      const stopMatch =
        !selectedStopFilter || String(stopId) === String(selectedStopFilter);

      const derivedStatus = getStopStatusMeta(stop).label;
      const statusMatch =
        !filters.status || derivedStatus.toUpperCase() === String(filters.status).toUpperCase();
      const typeMatch =
        !filters.type || String(stop.type || "STOP") === String(filters.type);

      if (!routeMatch || !stopMatch || !statusMatch || !typeMatch) {
        return false;
      }
    }

    // Search query (same for both)
    if (!searchQuery || searchQuery.trim() === "") {
      return true;
    }

    const query = searchQuery.toLowerCase().trim();

    const stopIdMatch =
      stop.stopId && stop.stopId.toLowerCase().includes(query);

    const stopNameMatch = stop.name && stop.name.toLowerCase().includes(query);

    const routeSearchMatch =
      stop.routes &&
      stop.routes.some(
        (route) => route.name && route.name.toLowerCase().includes(query)
      );

    const organizationMatch =
      stop.organization?.name &&
      stop.organization.name.toLowerCase().includes(query);

    const siteMatch =
      stop.site?.siteId && stop.site.siteId.toLowerCase().includes(query);

    return (
      stopIdMatch ||
      stopNameMatch ||
      routeSearchMatch ||
      organizationMatch ||
      siteMatch
    );
  });

  const handleViewAllStopsOnMap = () => {
    if (checkPermission("/api/stop/organization", "GET")) {
      navigate(isSchool ? "/stop/all/map?app=school" : "/stop/all/map", {
        state: { allStopsData: stopsData },
      });
    } else {
      console.log("No permission to view all stops on map");
    }
  };

  // Add stop handler
  const handleAddStop = () => {
    if (checkPermission("/api/stop", "POST")) {
      setShowCreateStop((isOpen) => !isOpen);
      setExpandedStopId(null);
    } else {
      console.log("No permission to add stop");
    }
  };

  const handleCreateStopComplete = () => {
    setShowCreateStop(false);
    loadStops(true);
  };

  useEffect(() => {
    const { openCreate, expandId } = readCreateIntent(location);
    if (!openCreate && !expandId) return;
    if (openCreate) {
      setExpandedStopId(null);
      setShowCreateStop(true);
    } else if (expandId) {
      setShowCreateStop(false);
      setExpandedStopId(expandId);
    }
    clearCreateIntent(navigate, location);
  }, [location, navigate]);

  const toggleExpandedStopRow = (stopId) => {
    setShowCreateStop(false);
    setExpandedStopId((currentId) =>
      String(currentId || "") === String(stopId || "") ? null : stopId,
    );
  };

  const handleUpdateStopComplete = () => {
    setExpandedStopId(null);
    loadStops(true);
  };

  // Handle delete dialog open
  const openDeleteModal = (stopId, stopData) => {
    if (checkPermission("/api/stop", "DELETE")) {
      setSelectedStopId(stopId);
      setSelectedStopData(stopData);
      setIsDeleteModalOpen(true);
    } else {
      console.log("No permission to delete stops");
    }
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedStopId(null);
    setSelectedStopData(null);
  };

  // Handle delete action
  const handleDelete = async () => {
    if (selectedStopId && checkPermission("/api/stop", "DELETE")) {
      try {
        const userState = localStorage.getItem("userState");
        const parsedUserState = JSON.parse(userState);
        const orgId = parsedUserState.user.organizationId;

        await axios.delete(`${config.baseURL}/stop`, {
          data: {
            id: selectedStopId,
            organizationId: orgId,
            deletedBy: userId,
          },
          withCredentials: true,
        });

        // Update local state
        const updatedStops = stopsData.filter(
          (stop) => stop.id !== selectedStopId
        );
        setStopsData(updatedStops);
        stopListCache.delete(String(getOrganizationId() || ""));
        setExpandedStopId(null);
        handleDeleteDialogClose();
      } catch (error) {
        console.error("Error deleting stop:", error);
        handleDeleteDialogClose();
      }
    }
  };

  // View stop details
  const handleViewStopDetails = (stopData) => {
    if (!checkPermission("/api/stop/id", "GET") || !stopData?.id) {
      return;
    }

    if (isSchool) {
      const allStops = Array.isArray(stopsData) ? stopsData : [];
      localStorage.setItem(
        `schoolStopMonitor:${stopData.id}`,
        JSON.stringify(stopData),
      );
      localStorage.setItem(
        "schoolStopMonitor:allStops",
        JSON.stringify(allStops),
      );
      window.open(
        `/school-stops/monitor/${stopData.id}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    navigate("/stop/track", {
      state: {
        selectedStopData: stopData,
        localStopsData: stopsData,
      },
    });
  };

  // Update stop details
  const handleUpdateStop = (stopId) => {
    if (checkPermission("/api/stop", "PUT")) {
      toggleExpandedStopRow(stopId);
    } else {
      console.log("No permission to update stop");
    }
  };

  const handleStopTableAction = (actionId, stopData) => {
    if (!stopData) return;
    const stopId = stopData.id;

    switch (actionId) {
      case "update":
        handleUpdateStop(stopId);
        break;
      case "view":
        handleViewStopDetails(stopData);
        break;
      case "delete":
        openDeleteModal(stopId, stopData);
        break;
      default:
        break;
    }
  };

  const activeStopsCount = stopsData.filter((stop) =>
    stopHasAssignedRoute(stop),
  ).length;
  const totalStopsCount = stopsData.length || 0;
  const totalRoutesCount = new Set(
    stopsData.flatMap((stop) =>
      getStopRoutes(stop)
        .map((route) => route.id || route.Id || route.ID)
        .filter(Boolean),
    ),
  ).size;

  const currentAvgDwell = stopsData.length
    ? stopsData.reduce((sum, stop) => sum + (matchStopStats(stop, visitStats).avgDwell || 0), 0) /
      stopsData.length
    : 0;
  const previousAvgDwell = stopsData.length
    ? stopsData.reduce(
        (sum, stop) => sum + (matchStopStats(stop, previousVisitStats).avgDwell || 0),
        0,
      ) / stopsData.length
    : 0;
  const dwellTrendValue =
    previousAvgDwell > 0
      ? (((currentAvgDwell - previousAvgDwell) / previousAvgDwell) * 100).toFixed(1)
      : null;

  const createAction = actionBarWidgets.find((widget) => widget.actionKey === "createStop");
  const monitorAction = actionBarWidgets.find((widget) => widget.actionKey === "monitorStops");
  const showAddButton = createAction
    ? hasLayoutPermission(createAction.permission, hasPermission)
    : checkPermission("/api/stop", "POST");
  const showTrackButton = monitorAction
    ? hasLayoutPermission(monitorAction.permission, hasPermission)
    : checkPermission("/api/stop/organization", "GET");
  const pageTitle = getPageTitleKey(moduleLayout) || "Stops";
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization?.name;

  const stopStatsCards = [
    {
      title: "Active Stops",
      value: `${activeStopsCount} / ${totalStopsCount}`,
      icon: HailIcon,
      tone: "blue",
    },
    {
      title: "Connected Routes",
      value: String(totalRoutesCount),
      icon: AltRouteIcon,
      tone: "purple",
    },
    {
      title: "Avg. Dwell Time",
      value: dashboardError ? "--" : formatStopDwell(currentAvgDwell),
      icon: AccessTimeIcon,
      tone: "red",
      trend: dwellTrendValue && !dashboardError ? `${Math.abs(dwellTrendValue)}%` : undefined,
      trendDirection: Number(dwellTrendValue) >= 0 ? "up" : "down",
    },
  ];

  const stopTypeOptions = Array.from(
    new Set(stopsData.map((stop) => stop.type || "STOP").filter(Boolean)),
  ).map((type) => ({ value: type, label: type }));

  const stopTableHeaders = isRouteEyeAdmin()
    ? [
        { key: "stopCode", label: "Stop ID", width: "110px" },
        { key: "stopName", label: "Stop Name", width: "160px" },
        { key: "organizationName", label: "Organization", width: "140px" },
        { key: "siteID", label: "Site", width: "110px" },
        { key: "routes", label: "Route(s)", width: "160px" },
        {
          key: "status",
          label: "Status",
          width: "110px",
          render: (row) => (
            <TableStatusBadge tone={row.statusTone}>{t(row.status)}</TableStatusBadge>
          ),
        },
        { key: "visits", label: "Today's Visits", width: "110px" },
        { key: "avgDwell", label: "Avg. Dwell Time", width: "130px" },
        { key: "lastVehicle", label: "Last Vehicle Visited", width: "150px" },
        {
          key: "lastUpdate",
          label: "Last Update",
          width: "130px",
          render: (row) => (
            <TableLastUpdate tone={row.lastUpdateTone}>{row.lastUpdate}</TableLastUpdate>
          ),
        },
      ]
    : [
        { key: "stopCode", label: "Stop ID", width: "110px" },
        { key: "stopName", label: "Stop Name", width: "160px" },
        { key: "routes", label: "Route(s)", width: "180px" },
        {
          key: "status",
          label: "Status",
          width: "110px",
          render: (row) => (
            <TableStatusBadge tone={row.statusTone}>{t(row.status)}</TableStatusBadge>
          ),
        },
        { key: "visits", label: "Today's Visits", width: "110px" },
        { key: "avgDwell", label: "Avg. Dwell Time", width: "130px" },
        { key: "lastVehicle", label: "Last Vehicle Visited", width: "150px" },
        {
          key: "lastUpdate",
          label: "Last Update",
          width: "130px",
          render: (row) => (
            <TableLastUpdate tone={row.lastUpdateTone}>{row.lastUpdate}</TableLastUpdate>
          ),
        },
      ];

  const stopTableFilters = [
    ...filterConfig.map((filter) => ({
      key: filter.id,
      label: `All ${filter.label || ""}`.trim(),
      value: filters[filter.id] || "",
      options: [
        { value: "", label: `All ${filter.label || "Options"}` },
        ...(filter.options || []),
      ],
      onChange: (value) => handleFilterChange({ ...filters, [filter.id]: value }),
    })),
    {
      key: "status",
      label: "All Status",
      value: filters.status || "",
      options: [
        { value: "", label: "All Status" },
        { value: "Active", label: "Active" },
        { value: "Unassigned", label: "Unassigned" },
      ],
      onChange: (value) => handleFilterChange({ ...filters, status: value }),
    },
    {
      key: "type",
      label: "All Types",
      value: filters.type || "",
      options: [{ value: "", label: "All Types" }, ...stopTypeOptions],
      onChange: (value) => handleFilterChange({ ...filters, type: value }),
    },
  ];

  const allStopTableData = formatDataForTable(filteredStopsData || []);
  const stopTableData = allStopTableData.slice(0, visibleStopCount);
  const hasMoreStops = visibleStopCount < allStopTableData.length;
  const handleShowMoreStops = () => {
    setVisibleStopCount((currentCount) =>
      Math.min(currentCount + STOP_TABLE_BATCH_SIZE, allStopTableData.length),
    );
  };
  const canUpdateStop = checkPermission("/api/stop", "PUT");
  const canDeleteStop = checkPermission("/api/stop", "DELETE");
  const canMonitorStop =
    checkPermission("/api/stop/id", "GET") || isSchool;
  const stopTableActions = canMonitorStop
    ? [
        {
          id: "view",
          label: "Monitor",
          icon: VisibilityOutlinedIcon,
          onClick: (row) => handleStopTableAction("view", row.originalData),
        },
      ]
    : [];

  const inventoryHeaders = [
    { key: "stopCode", label: "Stop ID" },
    { key: "stopName", label: "Stop Name" },
    { key: "routes", label: "Routes" },
  ];

  return (
    <>
      <SubTitleNew
        title={pageTitle}
        path={`${orgName || "organization"} > Stops`}
        add={!organizationInactive && showAddButton ? createAction?.labelKey || "Create Stops" : undefined}
        onAddClick={!organizationInactive && showAddButton ? handleAddStop : undefined}
        track={
          !organizationInactive && showTrackButton
            ? monitorAction?.labelKey || "Monitor Stops"
            : undefined
        }
        trackPath={
          !organizationInactive && showTrackButton
            ? isSchool
              ? "/stop/all/map?app=school"
              : "/stop/all/map"
            : undefined
        }
        onTrackClick={!organizationInactive && showTrackButton ? handleViewAllStopsOnMap : undefined}
        showDashboardControls={!organizationInactive}
        dateOptions={organizationInactive ? undefined : DASHBOARD_DATE_OPTIONS}
        dateError={dashboardError}
        onDateRangeChange={organizationInactive ? undefined : handleDashboardDateRangeChange}
      />
      <div className="vehicle-new-page-body">
        {showAddButton && showCreateStop && (
          <div className="vehicle-new-create-panel">
            <StopsAddMain
              embedded
              onCancel={() => setShowCreateStop(false)}
              onComplete={handleCreateStopComplete}
            />
          </div>
        )}
        {organizationInactive ? (
          <InactiveInventoryTable
            title="stopInventory"
            headers={inventoryHeaders}
            data={allStopTableData}
            loading={isLoading}
            emptyMessage={t("noDataFound")}
            itemLabel="stops"
          />
        ) : (
          <>
            {dashboardError && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="error" onClose={() => setDashboardError("")}>
                  {t(dashboardError)}
                </Alert>
              </Box>
            )}
            <div className="stats-card-grid vehicle-new-stats-grid stops-new-stats-grid">
              {stopStatsCards.map((card, index) => (
                <StatsCard
                  key={`${card.title}-${index}`}
                  title={card.title}
                  value={card.value}
                  icon={card.icon}
                  tone={card.tone}
                  trend={card.trend}
                  trendDirection={card.trendDirection}
                  loading={card.title === "Avg. Dwell Time" ? isHaltLoading : isLoading}
                />
              ))}
            </div>
            <StopsDashboard
              stopsData={filteredStopsData}
              haltReport={haltReport}
              dateOption={dashboardDateOption}
              customStartDate={dashboardCustomRange.start}
              customEndDate={dashboardCustomRange.end}
              siteId={filters.site}
              error={dashboardError}
            />
            <div className="table_container vehicle-new-table-container">
              <DynamicTable
                title="All Stops"
                className="vehicle-new-fleet-table"
                headers={stopTableHeaders}
                data={stopTableData}
                downloadData={allStopTableData}
                downloadFileName={isSchool ? "school-stops" : "stops"}
                filters={stopTableFilters}
                searchValue={searchQuery}
                onSearchChange={handleSearchChange}
                searchPlaceholder="Search stops"
                actions={stopTableActions}
                loading={isLoading}
                emptyMessage="No stops found"
                headerVariant="light"
                minWidth={1180}
                rowKey={(row) => row.id}
                onRowClick={
                  canUpdateStop
                    ? (row) => toggleExpandedStopRow(row.id)
                    : undefined
                }
                renderExpandedRow={
                  canUpdateStop
                    ? (row) => {
                        if (String(expandedStopId || "") !== String(row.id || "")) {
                          return null;
                        }

                        return (
                          <div className="vehicle-new-expanded-form">
                            <StopsAddMain
                              embedded
                              embeddedStopId={row.id}
                              onCancel={() => setExpandedStopId(null)}
                              onComplete={handleUpdateStopComplete}
                              onDelete={
                                canDeleteStop
                                  ? () => openDeleteModal(row.id, row.originalData)
                                  : undefined
                              }
                            />
                          </div>
                        );
                      }
                    : undefined
                }
                pagination={{
                  enabled: false,
                  pageSize: STOP_TABLE_BATCH_SIZE,
                  itemLabel: "stops",
                }}
              />
              {hasMoreStops ? (
                <div className="vehicle-show-more-footer">
                  <span>
                    {t("Showing")} {stopTableData.length} {t("of")}{" "}
                    {allStopTableData.length} {t("stops")}
                  </span>
                  <button type="button" onClick={handleShowMoreStops}>
                    {t("Show more")}
                  </button>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDelete}
        title="Delete Stop"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {selectedStopData?.name || t("this stop")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />
    </>
  );
}
